import React, { useEffect, useMemo, useState } from "react";
import { authHeader } from "../../auth";
import * as XLSX from "xlsx";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

/**
 * SwissSkiUpdateDialog — mise à jour des participants depuis la liste de
 * points SWISS-SKI (fichier d'export Excel / CSV / TXT). Ajouté le
 * 01.10.2026, pendant de "Mettre à jour tous les participants" (liste FIS).
 *
 * Colonnes du fichier Swiss-Ski (repérées par leur NOM, l'ordre peut varier) :
 *   PNr, Nachname, Vorname, JG, Gender, ClubNr, Clubname, RV, IReg, Nat,
 *   Kat, Punkte, Punkte1..Punkte5, FISCode, AnzStart, JC, SRC, SMC
 *
 * Le fichier est lu directement dans le navigateur (rien n'est stocké côté
 * serveur) ; seuls les participants modifiés sont enregistrés, via la même
 * fonction que les autres mises à jour (persistAthletes).
 *
 * Rapprochement d'un participant avec une ligne du fichier, dans l'ordre :
 *   1. son champ Code (fiscode) = PNr (n° Swiss-Ski)
 *   2. son champ Code = FISCode
 *   3. nom + prénom + année de naissance identiques (sans accents/majuscules)
 */

// Champs mis à jour (clé participant -> colonne Swiss-Ski).
const FIELDS = [
    { key: "points", label: "Points (colonne choisie ci-dessous)" },
    { key: "fromlist", label: "Liste (« Swiss-Ski » + nom du fichier)" },
    { key: "club", label: "Club (Clubname)" },
    { key: "association", label: "Association régionale (RV)" },
    { key: "category", label: "Catégorie (Kat)" },
    { key: "nation", label: "Nation (Nat)" },
    { key: "birthyear", label: "Année de naissance (JG)" },
    { key: "lastname", label: "Nom (Nachname)" },
    { key: "firstname", label: "Prénom (Vorname)" },
    { key: "gender", label: "Genre (Gender)" },
];

const POINT_COLUMNS = ["Punkte", "Punkte1", "Punkte2", "Punkte3", "Punkte4", "Punkte5"];

// Lit le fichier quel que soit son format : Excel, ou texte (CSV avec ; ou ,
// ou TSV) en UTF-8 ou Windows-1252 (accents des exports Windows).
export async function readSwissSkiFile(file) {
    const buf = await file.arrayBuffer();
    let workbook;
    if (/\.xlsx?$/i.test(file.name)) {
        workbook = XLSX.read(buf, { type: "array" });
    } else {
        let text;
        try {
            text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
        } catch {
            text = new TextDecoder("windows-1252").decode(buf);
        }
        workbook = XLSX.read(text, { type: "string", raw: true });
    }
    const ws = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
    // Noms de colonnes nettoyés (espaces, BOM).
    return rows.map((r) => {
        const clean = {};
        Object.entries(r).forEach(([k, v]) => {
            clean[String(k).replace(/^\uFEFF/, "").trim()] = typeof v === "string" ? v.trim() : v;
        });
        return clean;
    });
}

const norm = (s) =>
    String(s ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

// "12,34" ou "12.34" -> 12.34 ; vide ou >= 999 (pas de points) -> "".
function parsePoints(value) {
    const s = String(value ?? "").replace(",", ".").trim();
    if (!s) return "";
    const n = Number(s);
    return Number.isFinite(n) && n < 999 ? n : "";
}

export default function SwissSkiUpdateDialog({ open, onClose, athletes, persistAthletes, onDone }) {
    const [rows, setRows] = useState(null);
    const [fileName, setFileName] = useState("");
    const [readError, setReadError] = useState("");
    const [pointsColumn, setPointsColumn] = useState("Punkte");
    const [selected, setSelected] = useState({
        points: true,
        fromlist: true,
        club: false,
        association: false,
        category: false,
        nation: false,
        birthyear: false,
        lastname: false,
        firstname: false,
        gender: false,
    });
    const [fillCode, setFillCode] = useState(true);
    const [running, setRunning] = useState(false);

    // Liste Swiss-Ski stockée sur le serveur (07.10.2026) : importée une
    // fois, réutilisée ensuite comme la liste FIS — plus besoin de
    // réimporter le fichier à chaque mise à jour.
    const [listStatus, setListStatus] = useState(null);
    const [loadingList, setLoadingList] = useState(false);
    const api = (url, options = {}) =>
        fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } });

    const loadStatus = async () => {
        const r = await api("/api/team-check/swissski-list/status");
        const st = r.ok ? await r.json() : null;
        setListStatus(st);
        return st;
    };

    // Charge, depuis la liste en base, les lignes utiles aux participants
    // (n° Swiss-Ski / code FIS / nom) — même format que le fichier.
    const loadFromServer = async (st) => {
        setLoadingList(true);
        setReadError("");
        try {
            const codes = athletes.map((a) => String(a.fiscode || "").trim()).filter(Boolean);
            const lastnames = athletes.map((a) => String(a.lastname || "").trim()).filter(Boolean);
            const r = await api("/api/timing/swissski-list/bulk-lookup", { method: "POST", body: JSON.stringify({ codes, lastnames }) });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setRows(data.length ? data : []);
            setFileName(`liste en base${st?.listName ? ` — ${st.listName}` : ""}`);
            const available = POINT_COLUMNS.filter((c) => data[0] && c in data[0]);
            if (available.length && !available.includes(pointsColumn)) setPointsColumn(available[0]);
        } catch (err) {
            setRows(null);
            setReadError(`Lecture de la liste en base impossible : ${err.message}`);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        setRows(null);
        setFileName("");
        loadStatus().then((st) => st?.count > 0 && loadFromServer(st));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleFile = async (e) => {
        const file = e.target.files[0];
        e.target.value = "";
        if (!file) return;
        setReadError("");
        try {
            const data = await readSwissSkiFile(file);
            const missing = ["PNr", "Nachname", "Vorname"].filter((c) => !(data[0] && c in data[0]));
            if (!data.length) throw new Error("Fichier vide.");
            if (missing.length) throw new Error(`Colonnes manquantes : ${missing.join(", ")} — est-ce bien la liste Swiss-Ski ?`);
            // Le fichier est aussi ENREGISTRÉ sur le serveur : il remplace la
            // liste en base (utilisée ensuite sans réimport, et par la page
            // des entraîneurs).
            const up = await api("/api/team-check/swissski-list/import", {
                method: "POST",
                body: JSON.stringify({ rows: data, listName: file.name.replace(/\.[^.]+$/, "") }),
            });
            if (!up.ok) throw new Error(`enregistrement sur le serveur impossible (HTTP ${up.status})`);
            await loadStatus();
            setRows(data);
            setFileName(file.name);
            const available = POINT_COLUMNS.filter((c) => c in data[0]);
            if (!available.includes(pointsColumn)) setPointsColumn(available[0] || "Punkte");
        } catch (err) {
            setRows(null);
            setReadError(`Lecture impossible : ${err.message}`);
        }
    };

    // Index du fichier pour le rapprochement.
    const index = useMemo(() => {
        if (!rows) return null;
        const byPnr = new Map();
        const byFis = new Map();
        const byName = new Map();
        rows.forEach((r) => {
            if (r.PNr) byPnr.set(String(r.PNr), r);
            if (r.FISCode) byFis.set(String(r.FISCode), r);
            byName.set(`${norm(r.Nachname)}|${norm(r.Vorname)}|${String(r.JG || "").trim()}`, r);
        });
        return { byPnr, byFis, byName };
    }, [rows]);

    // Aperçu : qui sera trouvé, et comment.
    const matches = useMemo(() => {
        if (!index) return [];
        return athletes.map((a) => {
            const code = String(a.fiscode || "").trim();
            if (code && index.byPnr.has(code)) return { row: index.byPnr.get(code), how: "pnr" };
            if (code && index.byFis.has(code)) return { row: index.byFis.get(code), how: "fis" };
            const key = `${norm(a.lastname)}|${norm(a.firstname)}|${String(a.birthyear || "").trim()}`;
            if (index.byName.has(key)) return { row: index.byName.get(key), how: "name" };
            return { row: null, how: null };
        });
    }, [athletes, index]);

    const summary = useMemo(() => {
        const s = { pnr: 0, fis: 0, name: 0, none: 0 };
        matches.forEach((m) => (m.how ? s[m.how]++ : s.none++));
        return s;
    }, [matches]);

    const notFound = matches
        .map((m, i) => (m.row ? null : athletes[i]))
        .filter(Boolean)
        .slice(0, 10);

    const apply = async () => {
        if (!rows) return;
        if (
            !window.confirm(
                `Mettre à jour ${athletes.length - summary.none} participant(s) depuis la liste Swiss-Ski (${fileName}) ? ` +
                `Les champs cochés seront écrasés.`
            )
        ) {
            return;
        }
        setRunning(true);
        try {
            const listLabel = `Swiss-Ski ${fileName.replace(/\.[^.]+$/, "")}`;
            const updated = athletes.map((a, i) => {
                const r = matches[i]?.row;
                if (!r) return a;
                const next = { ...a };
                if (selected.points) next.points = parsePoints(r[pointsColumn]);
                if (selected.fromlist) next.fromlist = r._listName ? `Swiss-Ski ${r._listName}` : listLabel;
                if (selected.club) next.club = r.Clubname || a.club || "";
                if (selected.association) next.association = r.RV || a.association || "";
                if (selected.category) next.category = r.Kat || a.category || "";
                if (selected.nation) next.nation = r.Nat || a.nation || "";
                if (selected.birthyear) next.birthyear = r.JG || a.birthyear || "";
                if (selected.lastname) next.lastname = r.Nachname || a.lastname;
                if (selected.firstname) next.firstname = r.Vorname || a.firstname;
                if (selected.gender) next.gender = r.Gender || a.gender || "";
                // Participant trouvé par son nom mais sans code : on reprend
                // son n° Swiss-Ski, pour que les prochaines mises à jour le
                // retrouvent directement.
                if (fillCode && !String(a.fiscode || "").trim() && r.PNr) next.fiscode = String(r.PNr);
                return next;
            });
            const ok = await persistAthletes(updated);
            if (ok) {
                onDone(
                    `Mise à jour Swiss-Ski ✓ — ${athletes.length - summary.none} mis à jour ` +
                    `(${summary.pnr} par n° Swiss-Ski, ${summary.fis} par code FIS, ${summary.name} par nom), ` +
                    `${summary.none} non trouvé(s).`
                );
                onClose();
            }
        } finally {
            setRunning(false);
        }
    };

    const available = rows ? POINT_COLUMNS.filter((c) => c in rows[0]) : POINT_COLUMNS;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Mettre à jour depuis la liste Swiss-Ski</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Chaque participant est retrouvé par son code (= n° Swiss-Ski PNr ou code FIS), sinon par nom, prénom
                    et année de naissance.
                </Typography>

                <Alert severity={listStatus?.count ? "info" : "warning"} sx={{ mb: 2 }}>
                    {listStatus?.count
                        ? `Liste Swiss-Ski en base : ${listStatus.count} athlètes (${listStatus.listName || "—"}${
                            listStatus.updatedAt ? `, importée le ${new Date(listStatus.updatedAt).toLocaleDateString("fr-CH")}` : ""
                        }) — utilisée automatiquement.`
                        : "Aucune liste Swiss-Ski en base : importe le fichier une première fois."}
                    {loadingList ? " Chargement…" : ""}
                </Alert>

                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                    {listStatus?.count ? "Importer une nouvelle liste Swiss-Ski" : "Importer le fichier Swiss-Ski"}
                    <input type="file" accept=".xlsx,.xls,.csv,.txt,.tsv" hidden onChange={handleFile} />
                </Button>
                {fileName && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Source : {fileName}
                    </Typography>
                )}

                {readError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {readError}
                    </Alert>
                )}

                {rows && (
                    <>
                        <Alert severity={summary.none ? "warning" : "success"} sx={{ mt: 2 }}>
                            {rows.length} ligne(s) de la liste Swiss-Ski chargée(s). Participants trouvés :{" "}
                            <b>{athletes.length - summary.none}</b> / {athletes.length} ({summary.pnr} par n° Swiss-Ski,{" "}
                            {summary.fis} par code FIS, {summary.name} par nom)
                            {summary.none > 0 && (
                                <>
                                    <br />
                                    Non trouvés : {notFound.map((a) => `${a.lastname || ""} ${a.firstname || ""}`.trim()).join(", ")}
                                    {summary.none > notFound.length ? "…" : ""}
                                </>
                            )}
                        </Alert>

                        <FormControl size="small" sx={{ mt: 2, minWidth: 220 }}>
                            <InputLabel>Colonne de points</InputLabel>
                            <Select label="Colonne de points" value={pointsColumn} onChange={(e) => setPointsColumn(e.target.value)}>
                                {available.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 2 }} />
                        <FormControlLabel
                            sx={{ display: "block" }}
                            control={
                                <Checkbox
                                    checked={FIELDS.every((f) => selected[f.key])}
                                    indeterminate={FIELDS.some((f) => selected[f.key]) && !FIELDS.every((f) => selected[f.key])}
                                    onChange={(e) =>
                                        setSelected(Object.fromEntries(FIELDS.map((f) => [f.key, e.target.checked])))
                                    }
                                />
                            }
                            label={<b>Tout sélectionner</b>}
                        />
                        {FIELDS.map((f) => (
                            <FormControlLabel
                                key={f.key}
                                sx={{ display: "block" }}
                                control={
                                    <Checkbox
                                        checked={selected[f.key]}
                                        onChange={(e) => setSelected((prev) => ({ ...prev, [f.key]: e.target.checked }))}
                                    />
                                }
                                label={f.label}
                            />
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            sx={{ display: "block" }}
                            control={<Checkbox checked={fillCode} onChange={(e) => setFillCode(e.target.checked)} />}
                            label="Compléter le code avec le n° Swiss-Ski (PNr) s'il est vide"
                        />
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Points vides ou ≥ 999 : champ points laissé vide (pas de points).
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
                <Button
                    variant="contained"
                    onClick={apply}
                    disabled={!rows || running || athletes.length === summary.none || !Object.values(selected).some(Boolean)}
                >
                    {running ? "Mise à jour…" : "Lancer la mise à jour"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}